import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GenericDocument } from '../../data/models/document.model';
import DocumentDialog from './DocumentDialog';
import { GenericDocumentType } from '../../data/models/document-type.model';
import { DataGridPro, GridOverlay, GridCellParams } from '@mui/x-data-grid-pro';
import documentUtils, { DocumentWrapper } from '../../services/documentUtils';
import StopIcon from '@mui/icons-material/Stop';
import UploadIcon from '@mui/icons-material/CloudUpload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../ConfirmationDialog';
import { buildColumnVisibilityModel } from '../../services/dataGridUtils';
import * as providerSlice from '../../data/provider.slice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { DateTime } from 'luxon';

const PREFIX = 'DocumentList';

const classes = {
  container: `${PREFIX}-container`,
  tableContainer: `${PREFIX}-tableContainer`,
  table: `${PREFIX}-table`,
  current: `${PREFIX}-current`,
  missing: `${PREFIX}-missing`,
  expired: `${PREFIX}-expired`,
  expiring: `${PREFIX}-expiring`,
  actions: `${PREFIX}-actions`,
  legend: `${PREFIX}-legend`,
  litem: `${PREFIX}-litem`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  [`& .${classes.tableContainer}`]: {
    width: '100%',
    maxHeight: 380,
    overflowY: 'auto',
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.table}`]: {
    // width: '100%',
    // maxHeight: 380,
    // overflowY: 'auto',
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },

  [`& .${classes.current}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
  },

  [`& .${classes.missing}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.error.main,
  },

  [`& .${classes.expired}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textDecoration: 'line-through',
    cursor: 'pointer',
  },

  [`& .${classes.expiring}`]: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.palette.warning.main,
    cursor: 'pointer',
  },

  [`& .${classes.actions}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    width: '100%',
  },

  [`& .${classes.legend}`]: {
    display: 'flex',
    flexGrow: 1,
    justifyContent: 'space-between',
    marginLeft: theme.spacing(8),
    marginRight: theme.spacing(2),
  },

  [`& .${classes.litem}`]: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const DocumentLoadingOverlay = () => {
  return (
    <GridOverlay>
      <CircularProgress
        data-testid="progress"
        color="primary"
        style={{ position: 'absolute', bottom: 10 }}
      />
    </GridOverlay>
  );
};

interface DocumentListProps {
  providerID: number;
  documents: GenericDocument[];
  documentTypes: GenericDocumentType[];
  uploadingDocument: boolean;
  readOnly: boolean;
  allowView: boolean;
  save: (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => void;
  update: (
    documentId: number,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => void;
  doDelete: (id: number) => void;
}

export default function DocumentList({
  providerID,
  documents,
  documentTypes,
  uploadingDocument,
  readOnly,
  allowView,
  save,
  update,
  doDelete,
}: DocumentListProps) {
  const { t } = useTranslation('common');
  const uploadLabel = t('document.upload');
  const editLabel = t('document.edit');
  const deleteLabel = t('document.delete');
  const dispatch = useAppDispatch();

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [documentType, setDocumentType] = React.useState<number | undefined>();
  const [documentWrappers, setDocumentWrappers] = React.useState<
    DocumentWrapper[]
  >([]);

  const documentDownloadInfo = useAppSelector(
    providerSlice.selectDocumentDownloadInfo,
  );
  const loadingDocumentDownloadInfo = useAppSelector(
    providerSlice.selectLoadingDocumentDownloadInfo,
  );
  const loadedDocumentDownloadInfo = useAppSelector(
    providerSlice.selectLoadedDocumentDownloadInfo,
  );

  React.useEffect(() => {
    if (documents && documentTypes) {
      const w = documentUtils.getDocumentWrappers(documentTypes, documents);
      setDocumentWrappers(w);
    }
  }, [documents, documentTypes]);

  React.useEffect(() => {
    if (loadedDocumentDownloadInfo && documentDownloadInfo) {
      const a = document.createElement('a');
      a.href = documentDownloadInfo.presignedUrl;
      a.download = documentDownloadInfo.documentFileName;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      dispatch(providerSlice.setDocumentDownloadInfo(null));
      dispatch(providerSlice.setLoadedDocumentDownloadInfo(false));
    }
  }, [loadedDocumentDownloadInfo, documentDownloadInfo]);

  const saveDocument = () => {
    setDocumentType(undefined);
    setDialogOpen(true);
  };

  const uploadDocument = (docType: number) => {
    setEditingDocument(undefined);
    setDocumentType(docType);
    setDialogOpen(true);
  };

  const editDocument = (doc: DocumentWrapper) => {
    setEditingDocument(doc.document);
    setDocumentType(doc.documentTypeID);
    setDialogOpen(true);
  };

  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [deleteId, setDeleteId] = React.useState(0);
  const [editingDocument, setEditingDocument] = React.useState<
    GenericDocument | undefined
  >();

  const deleteDocument = (doc: DocumentWrapper) => {
    if (doc.document?.documentID) {
      setDeleteId(doc.document?.documentID);
      setConfirmOpen(true);
    }
  };

  const confirmDelete = () => {
    setConfirmOpen(false);
    doDelete(deleteId);
  };

  const cancelDelete = () => {
    setConfirmOpen(false);
  };

  const closeDialog = (
    file: any,
    typeId: number,
    signedOn: string | null,
    effectiveStart: string | null,
    effectiveEnd: string | null,
  ) => {
    setDialogOpen(false);
    setDocumentType(undefined);
    if (editingDocument && typeId) {
      update(
        editingDocument.documentID,
        typeId,
        signedOn,
        effectiveStart,
        effectiveEnd,
      );
    } else if (file && typeId) {
      save(file, typeId, signedOn, effectiveStart, effectiveEnd);
    }
    setEditingDocument(undefined);
  };

  const getClassName = (doc: DocumentWrapper) => {
    if (doc.expired) {
      return classes.expired;
    } else if (doc.expiring) {
      return classes.expiring;
    } else if (!doc.document) {
      return classes.missing;
    }
    return classes.current;
  };

  const downloadFile = (documentID: number) => {
    dispatch(
      providerSlice.getDocumentDownloadInfoAsync(providerID, documentID),
    );
  };

  const renderDocumentType = (params: GridCellParams): React.ReactElement => {
    const clsName = getClassName(params.row as DocumentWrapper);
    if (params.row.document && allowView) {
      return (
        <>
          <Link
            color="textPrimary"
            className={clsName}
            onClick={() => downloadFile(params.row.document.documentID)}
            underline="hover">
            {params.row.documentTypeName}
          </Link>
        </>
      );
    }
    return (
      <StyledBox className={clsName}>{params.row.documentTypeName}</StyledBox>
    );
  };

  const renderRequired = (params: GridCellParams): React.ReactElement => {
    return <>{params.row.required ? t('common.yes') : t('common.no')}</>;
  };

  const formatDate = (date: string): string => {
    if (date) {
      return DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
    }
    return '';
  };

  const renderSignedOnDate = (params: GridCellParams): React.ReactElement => {
    return <>{formatDate(params.row.document?.signedOnDate)}</>;
  };

  const renderStartDate = (params: GridCellParams): React.ReactElement => {
    return <>{formatDate(params.row.document?.effectiveStartDate)}</>;
  };

  const renderEndDate = (params: GridCellParams): React.ReactElement => {
    return <>{formatDate(params.row.document?.effectiveEndDate)}</>;
  };

  const renderButtons = (params: GridCellParams): React.ReactElement => {
    return (
      <>
        <IconButton
          edge="start"
          color="default"
          onClick={() => uploadDocument(params.row.documentTypeID)}
          aria-label="upload"
          data-testid="upload"
          size="large"
          disabled={uploadingDocument}>
          <Tooltip title={uploadLabel}>
            <UploadIcon />
          </Tooltip>
        </IconButton>
        <IconButton
          edge="start"
          color="default"
          onClick={() => editDocument(params.row as DocumentWrapper)}
          aria-label="edit"
          data-testid="edit"
          disabled={!params.row.document || uploadingDocument}
          size="large">
          <Tooltip title={editLabel}>
            <EditIcon />
          </Tooltip>
        </IconButton>
        <IconButton
          edge="start"
          color="primary"
          onClick={() => deleteDocument(params.row as DocumentWrapper)}
          aria-label="delete"
          data-testid="delete"
          disabled={!params.row.document || uploadingDocument}
          size="large">
          <Tooltip title={deleteLabel}>
            <DeleteIcon />
          </Tooltip>
        </IconButton>
      </>
    );
  };

  const columns = [
    { field: 'ID', headerName: 'ID', hide: true },
    {
      field: 'DocumentTypeName',
      headerName: t('document.type'),
      flex: 2,
      sortable: false,
      renderCell: renderDocumentType,
    },
    {
      field: 'Required',
      headerName: t('document.required'),
      flex: 1,
      sortable: false,
      renderCell: renderRequired,
    },
    {
      field: 'SignedOnDate',
      headerName: t('document.signedOn'),
      flex: 1,
      sortable: false,
      renderCell: renderSignedOnDate,
    },
    {
      field: 'EffectiveStartDate',
      headerName: t('document.effectiveStart'),
      flex: 1,
      sortable: false,
      renderCell: renderStartDate,
    },
    {
      field: 'EffectiveEndDate',
      headerName: t('document.effectiveEnd'),
      flex: 1,
      sortable: false,
      renderCell: renderEndDate,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 150,
      sortable: false,
      hide: readOnly,
      renderCell: renderButtons,
    },
  ];

  const columnVisibilityModel = React.useMemo(
    () => buildColumnVisibilityModel(columns as any),
    [readOnly, columns],
  );

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.tableContainer}>
        <DataGridPro
          slots={{
            loadingOverlay: DocumentLoadingOverlay,
          }}
          className={classes.table}
          getRowId={(row) => row.id}
          rows={documentWrappers}
          columns={columns as any}
          hideFooter={true}
          checkboxSelection={false}
          disableColumnMenu={true}
          disableColumnSelector={true}
          disableVirtualization={true}
          columnVisibilityModel={columnVisibilityModel}
          loading={uploadingDocument || loadingDocumentDownloadInfo}
        />
      </Box>
      <Box className={classes.actions}>
        {readOnly ? null : (
          <Button variant="contained" color="primary" onClick={saveDocument}>
            {t('document.addDocument')}
          </Button>
        )}
        <Box className={classes.legend}>
          <Box className={`${classes.current} ${classes.litem}`}>
            <StopIcon />
            {t('document.current')}
          </Box>
          <Box className={`${classes.missing} ${classes.litem}`}>
            <StopIcon />
            {t('document.missing')}
          </Box>
          <Box className={`${classes.expiring} ${classes.litem}`}>
            <StopIcon />
            {t('document.expiring')}
          </Box>
          <Box className={`${classes.expired} ${classes.litem}`}>
            <StopIcon />
            {t('document.expired')}
          </Box>
        </Box>
      </Box>
      <DocumentDialog
        open={dialogOpen}
        close={closeDialog}
        documentTypes={documentTypes}
        documentType={documentType}
        document={editingDocument}
      />
      <ConfirmationDialog
        title={t('common.confirmDelete')}
        message={t('document.confirmDelete')}
        open={confirmOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
    </StyledBox>
  );
}
